/*
 * File: useForgotPassword.ts
 * Author: Austin Robinson
 * Date Created: 9/7/2025
 * Copyright (c) DrivingDoc 2025
 */
import { AxiosError } from 'axios';
import { createMutation } from 'react-query-kit';

import { ErrorResponse, publicApi } from 'api';

/**
 * Parameters required to request a password reset email.
 */
export interface ForgotPasswordParams {
  /**
   * The email associated with the account.
   */
  email: string;
}

/**
 * Requests a password reset email for the specified account.
 * @param params - The forgot password parameters (email).
 * @returns A promise that resolves to `true` if the request was successful.
 * @throws {ErrorResponse} If the API request fails.
 */
async function forgotPassword({ email }: ForgotPasswordParams): Promise<boolean> {
  const result = await publicApi.post(`/public/auth/forgotPassword`, {
    email,
  });

  return result.status === 200;
}

/**
 * Custom React Query mutation hook for requesting a password reset email.
 * Integrates with `useErrorCollapse` for displaying errors in an MUI Collapse and Alert component.
 * @returns A mutation hook for requesting a password reset email.
 * @example
 * ```tsx
 * const { mutate, isLoading, error } = useForgotPassword({
 *   onSuccess: () => {
 *     console.log('Password reset email sent successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to send reset email:', error);
 *   },
 * });
 *
 * const handleSubmit = () => {
 *   mutate({ email: 'john@example.com' });
 * };
 * ```
 */
const useForgotPassword = createMutation<boolean, ForgotPasswordParams, AxiosError<Partial<ErrorResponse>>>({
  mutationFn: forgotPassword,
});

export { useForgotPassword };
